/**
 * InterviewFlow demo page — tabs for Permit Wizard, Return Assistant.
 * OpenCounter-inspired: clean, guided flows for permits and returns.
 */
import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Tabs,
  Tab,
  Paper,
  Stack,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Build as BuildIcon,
  Business as PermitsIcon,
  LocalShipping as ReturnsIcon,
  Person as PersonalIcon,
  PhotoCamera as CameraIcon,
  TrendingUp as LoanIcon,
  Checklist as ChecklistIcon,
  PrecisionManufacturing as DiagnosticIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import BrandLogo from '../../components/BrandLogo';
import PermitWizardDemo from './PermitWizardDemo';
import ReturnAssistantDemo from './ReturnAssistantDemo';
import PersonalAssistantDemo from './PersonalAssistantDemo';
import LoanBalanceDemo from './LoanBalanceDemo';
import EligibilityChecklistDemo from './EligibilityChecklistDemo';
import DiagnosticDiagramDemo from './DiagnosticDiagramDemo';
import CameraAnnotationDemo from './CameraAnnotationDemo';

const TEAL = '#0d7377';
const NAVY = '#1e3a5f';

const HASH_TO_TAB = { personal: 2, insurance: 3, loan: 4, eligibility: 5, diagnostic: 6 };

export default function InterviewFlowDemoPage() {
  const [tab, setTab] = useState(() => {
    const h = typeof window !== 'undefined' ? window.location.hash?.replace('#', '') : '';
    return HASH_TO_TAB[h] ?? 0;
  });

  React.useEffect(() => {
    const h = window.location.hash?.replace('#', '');
    if (HASH_TO_TAB[h] != null) setTab(HASH_TO_TAB[h]);
  }, []);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();

  const handleBack = () => navigate(-1);
  const handleLanding = () => navigate('/');
  const handleStudio = () => navigate('/studio');
  const handleSignup = () => navigate('/signup');

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#fafafa' }}>
      {/* Header */}
      <Box
        sx={{
          background: 'white',
          borderBottom: '1px solid',
          borderColor: 'divider',
          position: 'sticky',
          top: 0,
          zIndex: 10,
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Button size="small" startIcon={<BackIcon />} onClick={handleBack}>
                Back
              </Button>
              <BrandLogo size="small" orientation="horizontal" showTagline={false} iconSize={36} />
            </Box>
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" size="small" onClick={handleStudio} startIcon={<BuildIcon />}>
                Customize in Studio
              </Button>
              <Button variant="contained" size="small" onClick={handleSignup} sx={{ bgcolor: TEAL, '&:hover': { bgcolor: '#0a5c5f' } }}>
                Get Started
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ py: 4 }}>
        {/* Title */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: NAVY, mb: 1 }}>
            Try guided interview flows
          </Typography>
          <Typography variant="body1" color="text.secondary">
            See how a rich chat interface guides users through permits, returns, and more.
          </Typography>
        </Box>

        {/* Tabs */}
        <Paper sx={{ mb: 3, overflow: 'hidden' }}>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            variant={isMobile ? 'fullWidth' : 'standard'}
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              '& .MuiTab-root': { fontWeight: 600 },
              '& .Mui-selected': { color: TEAL },
            }}
          >
            <Tab icon={<PermitsIcon />} iconPosition="start" label="Permit Discovery" />
            <Tab icon={<ReturnsIcon />} iconPosition="start" label="Return Assistant" />
            <Tab icon={<PersonalIcon />} iconPosition="start" label="Personal Assistant" />
            <Tab icon={<CameraIcon />} iconPosition="start" label="Insurance / Camera" />
            <Tab icon={<LoanIcon />} iconPosition="start" label="Loan / DTI" />
            <Tab icon={<ChecklistIcon />} iconPosition="start" label="Eligibility" />
            <Tab icon={<DiagnosticIcon />} iconPosition="start" label="RMA Diagnostic" />
          </Tabs>

          <Box sx={{ p: 3 }}>
            {tab === 0 && <PermitWizardDemo onBack={handleBack} />}
            {tab === 1 && <ReturnAssistantDemo onBack={handleBack} />}
            {tab === 2 && <PersonalAssistantDemo onBack={handleBack} />}
            {tab === 3 && <CameraAnnotationDemo onBack={handleBack} />}
            {tab === 4 && <LoanBalanceDemo onBack={handleBack} />}
            {tab === 5 && <EligibilityChecklistDemo onBack={handleBack} />}
            {tab === 6 && <DiagnosticDiagramDemo onBack={handleBack} />}
          </Box>
        </Paper>

        {/* CTA */}
        <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'grey.50' }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: NAVY, mb: 1 }}>
            Customize in Studio
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Adapt these guided interviews with flows, apps, and forms when your process needs it.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="center">
            <Button
              variant="contained"
              startIcon={<BuildIcon />}
              onClick={handleStudio}
              sx={{ bgcolor: TEAL, '&:hover': { bgcolor: '#0a5c5f' } }}
            >
              Open Studio
            </Button>
            <Button
              variant="outlined"
              href="https://interviewflow.studio/publicSector/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="public-sector-outbound"
            >
              Public Sector (BaigLabs)
            </Button>
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
}
